import { useState } from "react";
import { css } from "@emotion/react";
import { usePurchase } from "@sharpana/payments/usePurchase";
import { zairaiProducts } from "../payments/catalog";
import { RestorePurchases } from "./RestorePurchases";

const page = css`
  min-height: 100%;
  padding: 2rem 1rem 3rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  color: var(--color-text);
`;

const heading = css`
  text-align: center;
  max-width: 34rem;
  h1 {
    margin: 0 0 0.5rem;
    font-size: 1.6rem;
  }
  p {
    margin: 0;
    color: var(--color-text-secondary);
    line-height: 1.5;
  }
`;

const cards = css`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  width: 100%;
  max-width: 44rem;
`;

const card = css`
  flex: 1 1 16rem;
  max-width: 20rem;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  h2 {
    margin: 0;
    font-size: 1.1rem;
  }
  ul {
    margin: 0;
    padding-left: 1.1rem;
    color: var(--color-text-secondary);
    line-height: 1.6;
  }
`;

const price = css`
  font-size: 1.4rem;
  font-weight: 600;
  color: var(--color-text);
`;

const priceUnknown = css`
  font-size: 0.9rem;
  color: var(--color-text-muted);
`;

const buyButton = css`
  margin-top: auto;
  background: var(--color-primary);
  color: var(--color-on-primary);
  border: 1px solid var(--color-primary-border);
  border-radius: 8px;
  padding: 0.65rem 1rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--color-primary-hover);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const webNotice = css`
  max-width: 34rem;
  text-align: center;
  color: var(--color-text-muted);
  font-size: 0.9rem;
  line-height: 1.5;
`;

/**
 * The coaching product's paywall, sold through the zairai mobile app.
 *
 * Purchases here go through the native store via the WebView wrapper: the
 * shared `usePurchase` hook handles the offerings handshake and the purchase
 * dispatch, and everything on this page — the wording, the layout, which
 * bullets each tier gets — is the coaching product's own.
 *
 * There is deliberately NO web (Stripe) checkout here yet, unlike boilit's
 * paywall. Adding one means a Stripe price wired to `productLogicalNameId`
 * and a form posting to `/api/create-stripe-checkout-session`, which the
 * coaching backend already mounts.
 */
export default function Paywall() {
  const { isInMobileApp, productFor, requestNativePayment } =
    usePurchase(zairaiProducts);
  const [pending, setPending] = useState<string | null>(null);

  const buy = (key: string) => {
    if (pending) return;
    setPending(key);

    // Returns false on web, where there is no native layer to ask. The button
    // then releases immediately rather than pretending a purchase started.
    if (!requestNativePayment(key)) {
      setPending(null);
      return;
    }

    // On the native path the store sheet takes over and, on success, the
    // wrapper navigates away to the post-checkout route — this component
    // unmounts and `pending` never matters. But a CANCELLED purchase sends
    // nothing back: the sheet closes, the page is still here, and the button
    // would read "Opening store…" forever. Release it so a second attempt is
    // possible.
    window.setTimeout(() => setPending(null), 8000);
  };

  const tiers = [
    {
      key: "creditPack",
      title: "Credit pack",
      // Keep these honest against the SkuCatalog row this tier maps to
      // (core/payments_logic/seed_skus.py, ZAIRAI_SKUS): 30 credits with
      // token_expiry_days = 200.  "Never expire" was wrong.
      bullets: [
        "30 session credits",
        "Valid for 200 days",
        "One-off purchase",
      ],
    },
    {
      key: "subscription",
      title: "Unlimited monthly",
      bullets: [
        "Unlimited coaching sessions",
        "Full access to the atlas",
        "Cancel any time",
      ],
    },
  ];

  return (
    <div css={page}>
      <header css={heading}>
        <h1>Keep going further</h1>
        <p>
          Pick up credits for occasional sessions, or go unlimited and work
          through the atlas at your own pace.
        </p>
      </header>

      <div css={cards}>
        {tiers.map((tier) => {
          const storeProduct = productFor(tier.key);
          return (
            <section css={card} key={tier.key}>
              <h2>{tier.title}</h2>
              {storeProduct?.priceString ? (
                <div css={price}>{storeProduct.priceString}</div>
              ) : (
                <div css={priceUnknown}>Price loads from the App Store</div>
              )}
              <ul>
                {tier.bullets.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
              <button
                css={buyButton}
                onClick={() => buy(tier.key)}
                disabled={!isInMobileApp || pending !== null}
              >
                {pending === tier.key ? "Opening store…" : "Choose"}
              </button>
            </section>
          );
        })}
      </div>

      {!isInMobileApp && (
        <p css={webNotice}>
          Purchases are currently available in the mobile app only.
        </p>
      )}

      <RestorePurchases />
    </div>
  );
}
